import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AnimatedCheck from '../components/AnimatedCheck';
import AnimatedError from '../components/AnimatedError';
import { Loader2 } from 'lucide-react';
import { extractApiErrorMessage, networkErrorMessage } from '../lib/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConfirmEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get('token') || '';
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [planId, setPlanId] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    if (calledRef.current) return;
    calledRef.current = true;
    confirmEmail();
  }, [token]);

  const confirmEmail = async () => {
    try {
      const res = await fetch(`${API_URL}/api/signup/confirm?token=${token}`);
      if (res.ok) {
        const data = await res.json();
        setStatus('success');
        setPlanId(data.plan_id || '');
        toast.success('Email confirmado!');
      } else {
        setStatus('error');
        toast.error(await extractApiErrorMessage(res, 'Token inválido ou expirado.'));
      }
    } catch (error) {
      setStatus('error');
      toast.error(networkErrorMessage(error, 'Não foi possível confirmar o email. Tente novamente.'));
    }
  };

  const handleCheckout = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stripe/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, email: '', host_url: window.location.origin })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          toast.error('Não foi possível abrir o pagamento. Tente novamente.');
        }
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao iniciar pagamento'));
      }
    } catch (error) {
      toast.error(networkErrorMessage(error, 'Não foi possível conectar ao Stripe. Tente novamente.'));
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AnimatedError />
            <h2 className="text-2xl font-bold mb-2">Link Inválido</h2>
            <p className="text-muted-foreground mb-6">
              Este link de confirmação é inválido ou já foi utilizado.
            </p>
            <Button onClick={() => navigate('/login')}>
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center">
          <AnimatedCheck />
          <h2 className="text-2xl font-bold mb-2">Conta Ativada!</h2>
          <p className="text-muted-foreground mb-6">
            Seu email foi confirmado e sua conta já está ativa. Faça login para começar a usar o Watizap.
          </p>
          <Button variant="outline" onClick={() => navigate('/login')}>
            Ir para Login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
